import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api-fetch';
import type {
  EmailCampaignDetail,
  EmailCampaignSummary,
  RecipientEmailHistoryEntry,
} from '../types/bulk-email-campaigns';

async function parseJsonResponse(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(`Server returned ${response.status} (expected JSON)`);
  }
  return response.json();
}

export function useEmailCampaignHistory() {
  const [campaigns, setCampaigns] = useState<EmailCampaignSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<EmailCampaignDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recipientLookupEmail, setRecipientLookupEmail] = useState('');
  const [recipientSends, setRecipientSends] = useState<RecipientEmailHistoryEntry[]>([]);
  const [recipientLookupLoading, setRecipientLookupLoading] = useState(false);
  const [recipientLookupActive, setRecipientLookupActive] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/bulk-email/campaigns?limit=50');
      const data = await parseJsonResponse(response);
      if (data.success) {
        setCampaigns((data.campaigns as EmailCampaignSummary[]) || []);
        setTotal(Number(data.total) || 0);
      }
    } catch (err) {
      console.error('Failed to load campaign history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaignDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setSelectedId(id);
    try {
      const pageSize = 500;
      let offset = 0;
      let campaign: EmailCampaignDetail['campaign'] | null = null;
      let allRecipients: EmailCampaignDetail['recipients'] = [];
      let recipientTotal = 0;

      while (offset < 20_000) {
        const response = await apiFetch(
          `/bulk-email/campaigns/${id}?recipientLimit=${pageSize}&recipientOffset=${offset}`
        );
        const data = await parseJsonResponse(response);
        if (!data.success) {
          setDetail(null);
          return;
        }
        if (!campaign) {
          campaign = data.campaign as EmailCampaignDetail['campaign'];
        }
        const batch = (data.recipients as EmailCampaignDetail['recipients']) || [];
        recipientTotal = Number(data.recipientTotal) || batch.length;
        allRecipients = [...allRecipients, ...batch];
        if (allRecipients.length >= recipientTotal || batch.length === 0) {
          break;
        }
        offset += pageSize;
      }

      if (campaign) {
        setDetail({
          campaign,
          recipients: allRecipients,
          recipientTotal,
        });
      } else {
        setDetail(null);
      }
    } catch (err) {
      console.error('Failed to load campaign detail:', err);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const clearDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
  }, []);

  const lookupRecipientHistory = useCallback(async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) return;

    setRecipientLookupLoading(true);
    setRecipientLookupActive(true);
    setRecipientLookupEmail(trimmed);
    setSelectedId(null);
    setDetail(null);

    try {
      const response = await apiFetch(
        `/bulk-email/recipient-history?email=${encodeURIComponent(trimmed)}`
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        toast.error(String(data.error || 'Could not find emails for this address'));
        setRecipientSends([]);
        return;
      }
      if (data.success) {
        setRecipientSends((data.sends as RecipientEmailHistoryEntry[]) || []);
      } else {
        setRecipientSends([]);
      }
    } catch (err) {
      console.error('Failed to lookup recipient history:', err);
      toast.error('Could not load email history. Please try again.');
      setRecipientSends([]);
    } finally {
      setRecipientLookupLoading(false);
    }
  }, []);

  const clearRecipientLookup = useCallback(() => {
    setRecipientLookupActive(false);
    setRecipientLookupEmail('');
    setRecipientSends([]);
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  return {
    campaigns,
    total,
    loading,
    detail,
    detailLoading,
    selectedId,
    fetchCampaigns,
    fetchCampaignDetail,
    clearDetail,
    recipientLookupEmail,
    recipientSends,
    recipientLookupLoading,
    recipientLookupActive,
    lookupRecipientHistory,
    clearRecipientLookup,
  };
}
